// Üretim Fişleri listesi kullanım rehberi. Metinler 'guides' çeviri alanında.
import type { TFunction } from 'i18next';
import type { GuideContent } from '../../components/common/UsageGuideDialog';

const G = 'production.orders';

export const productionOrdersGuide = (t: TFunction): GuideContent => ({
  layout: 'list',
  title: t(`${G}.title`, 'Üretim Fişleri nasıl kullanılır?'),
  intro: t(`${G}.intro`, 'Üretim fişi, reçetedeki malzemeleri stoktan düşüp üretilen ürünü (mamulü) stoğa ekleyen kayıttır. Bu sayfada tüm fişleri görür, yenisini açar ve durumlarını izlersiniz. Haritada bir bölgeye tıklayarak o adıma geçebilirsiniz.'),
  regionLabels: {
    header: t(`${G}.regions.header`, 'Başlık, Atölye ekranı'),
    stats: t(`${G}.regions.stats`, 'Özet kartları'),
    filters: t(`${G}.regions.filters`, 'Arama ve filtreler'),
    toolbar: t(`${G}.regions.toolbar`, 'Ayarlar / Yenile / Excel / Yeni'),
    table: t(`${G}.regions.table`, 'Fiş listesi'),
    pagination: t(`${G}.regions.pagination`, 'Sayfalama'),
  },
  steps: [
    {
      region: 'toolbar',
      title: t(`${G}.s1.title`, '1. Yeni fiş açın'),
      text: t(`${G}.s1.text`, '"Yeni Üretim Fişi" ile fiş sayfası açılır: ürünü, planlanan miktarı ve depoları seçersiniz, girdiler reçeteden gelir. Taslak olarak kaydedilen fiş stoğu etkilemez. Ayrıntılar fiş sayfasının kendi (i) rehberindedir.'),
    },
    {
      region: 'stats',
      title: t(`${G}.s2.title`, '2. Özet kartlarını okuyun'),
      text: t(`${G}.s2.text`, 'Kartlar seçili tarih aralığı ve depoya göre Taslak, Tamamlanan ve İptal fiş sayısını ve "Dönem üretim maliyeti"ni (tamamlanan fişlerin fiili maliyet toplamı) gösterir. Bir karta tıklamak listeyi o duruma göre süzer.'),
      tips: [
        t(`${G}.s2.tip1`, 'Uyarı simgesi bazı fişlerde döviz maliyeti olduğunu, bu tutarların çevrilmediğini belirtir.'),
      ],
    },
    {
      region: 'filters',
      title: t(`${G}.s3.title`, '3. Fişi bulun'),
      text: t(`${G}.s3.text`, 'Fiş no, ürün adı veya stok kodu ile arayın; durum (Taslak, Serbest, Üretimde, Tamamlandı, İptal), Başlangıç–Bitiş tarihi ve depo ile süzün. "Yalnız üst fişler" yarı mamuller için açılan alt fişleri gizler. "Filtreleri temizle" hepsini kaldırır.'),
    },
    {
      region: 'table',
      title: t(`${G}.s4.title`, '4. Listeyi okuyun ve fişi açın'),
      text: t(`${G}.s4.text`, 'Satırlarda Fiş No (alt fiş sayısı), Tarih, Ürün, Planlanan ve Üretilen miktar, Girdi / Çıktı Depo, Durum ve kalite durumu, operasyonlu fişte Adımlar ilerlemesi, Fiili ve Birim Maliyet görünür. Satıra tıklayınca fiş açılır.'),
      tips: [
        t(`${G}.s4.tip1`, 'Stok, fiş tamamlanınca değişir: girdiler girdi deposundan düşer, mamul çıktı deposuna girer. Operasyonlu fişte malzemeler her adım onayında düşer, mamul tüm adımlar bitip fiş tamamlanınca girer.'),
        t(`${G}.s4.tip2`, 'Tamamlanan fiş iptal edilirse girdiler depoya geri alınır, mamul depodan düşülür.'),
      ],
    },
    {
      region: 'header',
      title: t(`${G}.s5.title`, '5. Atölye ekranı ve ayarlar'),
      text: t(`${G}.s5.text`, '"Atölye ekranı" operasyonlu fişlerin istasyonlarda bekleyen adımlarını gösterir. Dişli simgesindeki "Üretim Ayarları"nda genel gider oranını (%) belirlersiniz; genel gider, malzeme + makine + işçilik toplamına bu oranla eklenir.'),
      tips: [
        t(`${G}.s5.tip1`, '"İşçilik ve genel gideri standart maliyete dahil et" kapalıysa standart maliyet yalnız malzemeden hesaplanır; fiili maliyete genel gider yine eklenir.'),
        t(`${G}.s5.tip2`, 'Lot takibi açıksa "Kalite uyarılı üretimin lotlarını bloke et" ile kalite uyarısıyla tamamlanan üretimin lotları bekletmeye alınır.'),
      ],
    },
    {
      region: 'pagination',
      title: t(`${G}.s6.title`, '6. Dışa aktarın ve sayfalar arasında gezinin'),
      text: t(`${G}.s6.text`, '"Excel\'e aktar" filtrelere uyan fişleri indirir; en fazla ilk 5000 kayıt aktarılır. Sayfa başına 10, 25, 50 veya 100 satır seçebilirsiniz. Başlıktaki (i) bu rehberi yeniden açar.'),
    },
    {
      region: 'stats',
      title: t(`${G}.s7.title`, '7. Sabit Üretimler sekmesi'),
      text: t(`${G}.s7.text`, 'Başlığın altındaki "Sabit Üretimler (N)" sekmesi her gün aynı miktarda üretilen ürünlerin hazır tanımlarını listeler; N tanım sayısıdır. Ürün veya ad ile arayın, şube ve depo ile süzün. Satırlarda bir üretimdeki miktar, girdi → çıktı deposu, şube, son üretim (ör. "Bugün 10:30 · 2 kez · URT-…") ve toplam üretim sayısı görünür. "Üret" kaç kez üretileceğini ve düşecek malzemeleri soran onay penceresini açar; onaylayınca fiş açılır ve hemen tamamlanır.'),
      tips: [
        t(`${G}.s7.tip1`, 'Tanım Basit Üretim Reçeteleri › ⋮ › Sabit üretim fişi tanımla ile yapılır. Satırdaki ⋮ menüsünden düzenler ya da silersiniz; silmek açılmış fişleri etkilemez.'),
        t(`${G}.s7.tip2`, 'En son üretilen en üstte durur; hiç üretilmeyenler sondadır. Seçili sekme adres çubuğunda saklanır, sayfayı yenileyince kaybolmaz.'),
        t(`${G}.s7.tip3`, 'Şube yöneticisi yalnız kendi şubesinin depolarına ait sabit fişleri görür. Üretmek için "Sabit üretim yapabilir" izni ya da hem fiş oluşturma hem fiş tamamlama izni gerekir; düzenleyip silmek için fiş oluşturma izni gerekir.'),
      ],
    },
  ],
});
